import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { _T, C } from '../shared/theme';
import { s_PB, s_GB } from '../shared/styles';
import { AI_PARTNER } from '../shared/constants';
import { fetchUsers, createGameSession, getUserId } from '../shared/dbService';
import Avatar from '../components/ui/Avatar';
import StatusDot from '../components/ui/StatusDot';
import GameRoom from '../features/games/GameRoom';

const SOCIAL_APPS = [
  { id: 'whatsapp',  label: 'WhatsApp',  color: '#25D366', icon: '💬', href: (url, msg) => `https://wa.me/?text=${encodeURIComponent(msg + ' ' + url)}` },
  { id: 'telegram',  label: 'Telegram',  color: '#229ED9', icon: '✈️', href: (url, msg) => `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(msg)}` },
  { id: 'signal',    label: 'Signal',    color: '#3A76F0', icon: '🔵', href: (url, msg) => `https://signal.me/#p=${encodeURIComponent(url)}` },
  { id: 'viber',     label: 'Viber',     color: '#7360F2', icon: '📳', href: (url, msg) => `viber://forward?text=${encodeURIComponent(msg + ' ' + url)}` },
  { id: 'snapchat',  label: 'Snapchat',  color: '#FFFC00', icon: '👻', href: (url, msg) => `https://www.snapchat.com/scan?attachmentUrl=${encodeURIComponent(url)}` },
];

function MultiplayerInviteModal({ lang, title, icon, allUsers, friends, onInvite, onClose, defaultFriend }) {
  const nl = lang === "nl";
  const [selected, setSelected] = useState(defaultFriend?.id || null);
  const [mode, setMode] = useState("simultaneous");
  const [tab, setTab] = useState("friends"); // "friends" | "invite"
  const [dbFriends, setDbFriends] = useState([]);
  const [inviteLink, setInviteLink] = useState(null);
  const [inviteToken, setInviteToken] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [roomToken, setRoomToken] = useState(null);
  const [roomLoading, setRoomLoading] = useState(false);

  // Read identity from localStorage so game components don't need to pass it
  const currentUserId = getUserId();
  const userName = (() => { try { return JSON.parse(localStorage.getItem('sl_name') || '""') || ''; } catch { return ''; } })();

  // Fetch real friend profiles from DB
  useEffect(() => {
    if (!friends || friends.length === 0) return;
    fetchUsers(currentUserId).then(users => {
      if (Array.isArray(users)) setDbFriends(users.filter(u => friends.includes(u.id)));
    }).catch(() => {});
  }, [currentUserId, friends]); // eslint-disable-line react-hooks/exhaustive-deps

  // Merge DB friends with allUsers friends (dedupe by id)
  const localFriends = (allUsers || []).filter(u => friends.includes(u.id) && u.status !== 'resting');
  const merged = [...dbFriends];
  localFriends.forEach(u => { if (!merged.find(x => x.id === u.id)) merged.push(u); });
  const availableUsers = [...merged, AI_PARTNER];

  const handleInvite = async () => {
    if (!selected) return;
    const user = availableUsers.find(u => u.id === selected);
    if (!user) return;
    if (user.isAI) {
      // AI partner: start immediately, no room needed
      onInvite(user, mode);
      return;
    }
    // Real friend: create game room
    setRoomLoading(true);
    const token = await createGameSession(title, mode, currentUserId, userName, user.id);
    setRoomLoading(false);
    if (token) setRoomToken(token);
  };

  const generateInviteLink = async () => {
    setGenerating(true);
    try {
      const token = await createGameSession(title, mode, currentUserId, userName);
      if (token) {
        setInviteToken(token);
        setInviteLink(`https://senselifting.com/?gameinvite=${token}`);
      }
    } catch {}
    setGenerating(false);
  };

  const buildShareMsg = (app) => {
    return nl
      ? `${userName || 'Iemand'} nodigt je uit voor een spelletje ${title} op Sense Lifting! Klik om direct mee te spelen:`
      : `${userName || 'Someone'} invited you to play ${title} on Sense Lifting! Click to join instantly:`;
  };

  return ReactDOM.createPortal(
    <div style={{ position: "fixed", inset: 0, zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: "16px 16px" }}>
      <div onClick={!roomToken ? onClose : undefined} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.75)" }} />
      <div className="fadeUp" style={{ position: "relative", zIndex: 1001, background: _T.bg2, border: `1px solid ${_T.cardBorderTop}`, borderRadius: 24, padding: 24, width: "100%", maxWidth: 480, maxHeight: "85vh", overflowY: "auto" }}>
        <button onClick={onClose} style={{position:"absolute",top:14,right:14,background:"transparent",border:"none",color:C.silver,fontSize:20,cursor:"pointer",padding:4,lineHeight:1,zIndex:2}}>✕</button>
        <div style={{ width: 40, height: 4, borderRadius: 2, background: _T.cardBorderBot, margin: "0 auto 20px" }} />

        {/* Game Room view — shown once a room token is created */}
        {roomToken ? (
          <GameRoom
            token={roomToken}
            lang={lang}
            currentUser={{ id: currentUserId, name: userName }}
            isInviter={true}
            gameTitle={title}
            gameIcon={icon}
            onStart={(partner, m) => { onInvite({ ...partner, _isInviter: true, _token: roomToken }, m); }}
            onLeave={() => setRoomToken(null)}
          />
        ) : (
          <>
        <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 16 }}>
          <div style={{ fontSize: 28 }}>{icon}</div>
          <div>
            <div style={{ fontFamily: "Georgia,serif", fontSize: 17, color: C.white }}>{nl ? "Samen spelen" : "Play Together"}</div>
            <div style={{ fontSize: 12, color: C.silver }}>{title}</div>
          </div>
        </div>

        {/* Tab selector */}
        <div style={{ display: "flex", background: _T.bg4, borderRadius: 12, padding: 3, marginBottom: 16, gap: 2 }}>
          {[
            { id: "friends", label: nl ? "👥 Vrienden" : "👥 Friends" },
            { id: "invite",  label: nl ? "📲 Uitnodigen" : "📲 Invite" },
          ].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{ flex: 1, background: tab === t.id ? `linear-gradient(135deg,${_T.grad1},${_T.grad2})` : "transparent", border: "none", borderRadius: 9, padding: "8px 4px", cursor: "pointer", color: tab === t.id ? "#0a1220" : C.silver, fontSize: 12, fontWeight: tab === t.id ? 700 : 400 }}>
              {t.label}
            </button>
          ))}
        </div>

        {tab === "friends" && (
          <>
            <div style={{ fontSize: 11, color: C.silver, marginBottom: 8, letterSpacing: 1 }}>{nl ? "KIES EEN PARTNER" : "CHOOSE A PARTNER"}</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
              {availableUsers.map(u => (
                <div key={u.id} onClick={() => setSelected(u.id)} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", borderRadius: 14, background: selected === u.id ? `${u.color || _T.accent}18` : _T.bg4, border: `1.5px solid ${selected === u.id ? u.color || _T.accent : _T.cardBorderBot}`, cursor: "pointer", transition: "all 0.15s" }}>
                  <div style={{ position: "relative" }}>
                    <Avatar user={u} size={36} profileImage={u.picture || null} />
                    {!u.isAI && <span style={{ position: "absolute", bottom: 0, right: 0 }}><StatusDot status={u.status || "offline"} size={10} /></span>}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ color: C.white, fontSize: 13, fontWeight: 600 }}>{u.name}</div>
                    <div style={{ fontSize: 11, color: C.silver }}>{u.isAI ? "🦋 Sense Lifting Coach" : "👤 Friend"}</div>
                  </div>
                  {selected === u.id && <div style={{ color: u.color || _T.accent, fontSize: 16 }}>✓</div>}
                </div>
              ))}
              {availableUsers.length === 1 && (
                <div style={{ fontSize: 12, color: C.silver, textAlign: "center", padding: "8px 0" }}>
                  {nl ? "Voeg vrienden toe of gebruik 'Uitnodigen'" : "Add friends or use 'Invite'"}
                </div>
              )}
            </div>

            <div style={{ fontSize: 11, color: C.silver, marginBottom: 8, letterSpacing: 1 }}>{nl ? "SPELMODUS" : "GAME MODE"}</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 20 }}>
              {[
                { id: "simultaneous", icon: "⚡", label: nl ? "Gelijktijdig" : "Simultaneous", desc: nl ? "Beide spelen tegelijk" : "Both play at once" },
                { id: "turnbased", icon: "🔄", label: nl ? "Beurtelings" : "Turn-based", desc: nl ? "Jij eerst, dan de ander" : "You first, then the other" },
              ].map(m => (
                <div key={m.id} onClick={() => setMode(m.id)} style={{ padding: 12, borderRadius: 14, background: mode === m.id ? `${_T.accent}15` : _T.bg4, border: `1.5px solid ${mode === m.id ? _T.accent : _T.cardBorderBot}`, cursor: "pointer", textAlign: "center" }}>
                  <div style={{ fontSize: 20, marginBottom: 4 }}>{m.icon}</div>
                  <div style={{ color: mode === m.id ? _T.accent : C.white, fontSize: 12, fontWeight: 600 }}>{m.label}</div>
                  <div style={{ color: C.silver, fontSize: 11, marginTop: 2, lineHeight: 1.4 }}>{m.desc}</div>
                </div>
              ))}
            </div>
            <button style={s_PB({ width: "100%", opacity: (selected && !roomLoading) ? 1 : 0.4, fontSize: 14 })} disabled={!selected || roomLoading} onClick={handleInvite}>
              {roomLoading
                ? (nl ? "Kamer aanmaken..." : "Creating room...")
                : selected === AI_PARTNER.id
                  ? (nl ? "Speel met AI →" : "Play with AI →")
                  : (nl ? "Starten →" : "Start →")}
            </button>
          </>
        )}

        {tab === "invite" && (
          <>
            <div style={{ fontSize: 13, color: C.silver2, marginBottom: 16, lineHeight: 1.6 }}>
              {nl
                ? "Genereer een uitnodigingslink en stuur hem via je favoriete app. Mensen zonder account hoeven alleen in te loggen — ze gaan direct het spel in."
                : "Generate an invite link and share it via your favourite app. People without an account only need to sign in — they jump straight into the game."}
            </div>

            {!inviteLink ? (
              <button onClick={generateInviteLink} disabled={generating} style={s_PB({ width: "100%", fontSize: 14, opacity: generating ? 0.6 : 1, marginBottom: 20 })}>
                {generating ? (nl ? "Genereren..." : "Generating...") : (nl ? "🔗 Maak uitnodigingslink" : "🔗 Create Invite Link")}
              </button>
            ) : (
              <>
                <div style={{ background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 12, padding: "10px 14px", marginBottom: 14, display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ flex: 1, color: C.silver2, fontSize: 11, wordBreak: "break-all" }}>{inviteLink}</div>
                  <button onClick={() => { navigator.clipboard?.writeText(inviteLink); }} style={{ ...s_GB({ padding: "5px 10px", fontSize: 11, borderRadius: 8 }), flexShrink: 0 }}>
                    {nl ? "Kopieer" : "Copy"}
                  </button>
                </div>
                <div style={{ fontSize: 11, color: C.silver, letterSpacing: 1, marginBottom: 10 }}>{nl ? "DEEL VIA" : "SHARE VIA"}</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
                  {SOCIAL_APPS.map(app => (
                    <a
                      key={app.id}
                      href={app.href(inviteLink, buildShareMsg(app))}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ flex: "1 1 calc(50% - 4px)", minWidth: 120, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, background: app.color + "22", border: `1px solid ${app.color}55`, borderRadius: 12, padding: "10px 12px", color: app.id === 'snapchat' ? '#b8a800' : app.color, fontSize: 13, fontWeight: 600, textDecoration: "none", cursor: "pointer" }}
                    >
                      {app.icon} {app.label}
                    </a>
                  ))}
                </div>
                <button
                  onClick={() => setRoomToken(inviteToken)}
                  style={s_PB({ width: "100%", fontSize: 14 })}
                >
                  {nl ? "⏳ Ga naar wachtkamer →" : "⏳ Enter Waiting Room →"}
                </button>
              </>
            )}
          </>
        )}
          </>
        )}
      </div>
    </div>,
    document.body
  );
}

export default MultiplayerInviteModal;
